import { toggleList } from '@platejs/list';
import { insertEmptyCodeBlock } from '@platejs/code-block';
import { insertToc } from '@platejs/toc';

export interface SlashItem {
    id: string;
    label: string;
    keywords?: string[];
    onSelect: (editor: any, ctx?: { uploadSource?: string }) => void;
}

function toggleBlock(editor: any, blockType: string): void {
    try {
        editor?.tf?.[blockType]?.toggle?.();
    } catch {
        /* defensive */
    }
}

const HEADING_TYPES = ['h1', 'h2', 'h3'];

function clearHeadingSizeOverrides(editor: any): void {
    try {
        editor?.tf?.removeMarks?.('fontSize');
        editor?.tf?.unsetNodes?.(['lineHeight', 'fontSize'], {
            match: (n: any) => HEADING_TYPES.includes(n?.type),
            mode: 'lowest',
        });
    } catch {
        /* defensive */
    }
}

function applyHeading(editor: any, type: 'h1' | 'h2' | 'h3'): void {
    try {
        editor?.tf?.[type]?.toggle?.();
        clearHeadingSizeOverrides(editor);
    } catch {
        /* defensive */
    }
}

export const SLASH_ITEMS: SlashItem[] = [
    {
        id: 'h1',
        label: 'Heading 1',
        keywords: ['heading', 'h1', 'title', 'big'],
        onSelect: (editor) => applyHeading(editor, 'h1'),
    },
    {
        id: 'h2',
        label: 'Heading 2',
        keywords: ['heading', 'h2', 'subtitle', 'medium'],
        onSelect: (editor) => applyHeading(editor, 'h2'),
    },
    {
        id: 'h3',
        label: 'Heading 3',
        keywords: ['heading', 'h3', 'small', 'section'],
        onSelect: (editor) => applyHeading(editor, 'h3'),
    },
    {
        id: 'ul',
        label: 'Bulleted list',
        keywords: ['bullet', 'list', 'unordered', 'ul', '-'],
        onSelect: (editor) => {
            try {
                (toggleList as any)(editor, { listStyleType: 'disc' });
            } catch {
                /* defensive */
            }
        },
    },
    {
        id: 'ol',
        label: 'Numbered list',
        keywords: ['numbered', 'list', 'ordered', 'ol', '1.'],
        onSelect: (editor) => {
            try {
                (toggleList as any)(editor, { listStyleType: 'decimal' });
            } catch {
                /* defensive */
            }
        },
    },
    {
        id: 'blockquote',
        label: 'Quote',
        keywords: ['quote', 'blockquote', 'callout', '>'],
        onSelect: (editor) => toggleBlock(editor, 'blockquote'),
    },
    {
        id: 'callout',
        label: 'Callout',
        keywords: ['callout', 'note', 'info', 'tip', 'warning'],
        onSelect: (editor) => toggleBlock(editor, 'callout'),
    },
    {
        id: 'hr',
        label: 'Divider',
        keywords: ['divider', 'hr', 'rule', 'line', '---'],
        onSelect: (editor) => {
            try {
                editor?.tf?.insertNodes?.({ type: 'hr', children: [{ text: '' }] });
            } catch {
                /* defensive */
            }
        },
    },
    {
        id: 'table',
        label: 'Table',
        keywords: ['table', 'grid', 'rows', 'columns'],
        onSelect: (editor) => {
            try {
                editor?.tf?.insert?.table?.({ rowCount: 3, colCount: 3 });
            } catch {
                /* defensive */
            }
        },
    },
    {
        id: 'code_block',
        label: 'Code',
        keywords: ['code', 'block', 'pre', 'snippet', 'monospace', '```'],
        onSelect: (editor) => {
            try {
                (insertEmptyCodeBlock as any)(editor, { defaultType: 'p' });
            } catch {
                /* defensive */
            }
        },
    },
    {
        id: 'toc',
        label: 'Table of contents',
        keywords: ['toc', 'contents', 'headings', 'outline', 'navigation'],
        onSelect: (editor) => {
            try {
                (insertToc as any)(editor);
            } catch {
                /* defensive */
            }
        },
    },
    {
        id: 'image',
        label: 'Image',
        keywords: ['image', 'picture', 'photo', 'img'],
        onSelect: (editor, ctx) => {
            try {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/png,image/jpeg,image/gif,image/webp,image/svg+xml';
                input.onchange = async () => {
                    try {
                        const file = input.files?.[0];
                        if (!file) return;
                        const { handleImageInsert } = await import('../ImagePlugin/handleImageInsert');
                        handleImageInsert(file, editor, ctx?.uploadSource);
                    } catch (e) {
                        console.error('[slash:image] failed to insert image', e);
                    }
                };
                input.click();
            } catch {
                /* defensive */
            }
        },
    },
];

export function filterSlashItems(items: SlashItem[], query: string): SlashItem[] {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((item) => {
        const inLabel = item.label.toLowerCase().includes(q);
        const inKeywords = item.keywords?.some((k) => k.toLowerCase().includes(q)) ?? false;
        return inLabel || inKeywords;
    });
}
